import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import { useAutoRefresh } from "./hooks/useAutoRefresh";
import { useTokenValidation } from "./hooks/useTokenValidation";

// Import all pages
import Login from './pages/login';
import Dashboard from './pages/dashboard';
import Expenses from './pages/expenses';
import FinancialReports from './pages/financial-reports';
import ChartOfAccounts from './pages/chart-of-accounts';
import BankReconciliation from './pages/bank-reconciliation';
import Invoices from './pages/invoices';
import Settings from './pages/settings';
import TaxManagement from './pages/tax-management';
import JournalEntries from './pages/journal-entries';
import GeneralLedger from './pages/general-ledger';
import CustomerManagement from './pages/customer-management';
import FinancialInsights from './pages/financial-insights';
import UserManagement from './pages/user-management';
import CompanyManagement from './pages/company-management';
import NotFound from './pages/NotFound';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 2 times for other errors
        return failureCount < 2;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Component to handle hooks that need AuthProvider context
function AuthenticatedApp() {
  // Initialize token validation (needs to be inside AuthProvider)
  useTokenValidation();

  return (
    <Routes>
      {/* Default route - redirect to login */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      
      {/* Public route */}
      <Route path="/login" element={<Login />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
      <Route path="/financial-reports" element={<ProtectedRoute><FinancialReports /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><FinancialReports /></ProtectedRoute>} />
      <Route path="/chart-of-accounts" element={<ProtectedRoute><ChartOfAccounts /></ProtectedRoute>} />
      <Route path="/accounts" element={<ProtectedRoute><ChartOfAccounts /></ProtectedRoute>} />
      <Route path="/bank-reconciliation" element={<ProtectedRoute><BankReconciliation /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/tax-management" element={<ProtectedRoute><TaxManagement /></ProtectedRoute>} />
      <Route path="/journal-entries" element={<ProtectedRoute><JournalEntries /></ProtectedRoute>} />
      <Route path="/general-ledger" element={<ProtectedRoute><GeneralLedger /></ProtectedRoute>} />
      <Route path="/customer-management" element={<ProtectedRoute><CustomerManagement /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><CustomerManagement /></ProtectedRoute>} />
      <Route path="/financial-insights" element={<ProtectedRoute><FinancialInsights /></ProtectedRoute>} />
      <Route path="/insights" element={<ProtectedRoute><FinancialInsights /></ProtectedRoute>} />
      <Route path="/user-management" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
      <Route path="/company-management" element={<ProtectedRoute><CompanyManagement /></ProtectedRoute>} />
      <Route path="/companies" element={<ProtectedRoute><CompanyManagement /></ProtectedRoute>} />
      
      {/* 404 route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

// App content component to use hooks inside QueryClientProvider
function AppContent() {
  // Initialize auto-refresh functionality
  useAutoRefresh();

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <ScrollToTop />
          <AuthenticatedApp />
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'hsl(var(--card))',
            color: 'hsl(var(--foreground))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '0.75rem',
            fontSize: '0.875rem',
          },
          success: {
            iconTheme: { primary: 'hsl(var(--success, 142 76% 36%))', secondary: 'white' },
          },
          error: {
            iconTheme: { primary: 'hsl(var(--destructive))', secondary: 'white' },
          },
        }}
      />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
